import { DateTime } from "luxon";

import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { Encounter } from "../MedicalSummary/Encounter";
import { MedicalItemImmunization } from "../MedicalSummary/MedicalItemImmunization";
import { MedicalItemScreeningTest } from "../MedicalSummary/MedicalItemScreeningTest";
import { MedicalItemVitalSign } from "../MedicalSummary/MedicalItemVitalSign";
import { MedicalItemScreeningTestResponse } from "../MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { ObstetricalFileVitalSign } from "./ObstetricalFileVitalSign";

class ObstetricalFileMedicalSummary {
    private encounters: Encounter[];
    private screenings: MedicalItemScreeningTestResponse[];
    private vitalSigns: MedicalItemVitalSign[];
    private immunizations: MedicalItemImmunization[];

    public constructor(patientMedicalSummary: IPatientMedicalSummaryModel, immunizations: MedicalItemImmunization[]) {
        this.encounters = patientMedicalSummary?.get("Encounters")?.toArray() ?? [];
        this.screenings = patientMedicalSummary?.get("MedicalItemsScreeningTest")?.toArray() ?? [];
        this.vitalSigns = patientMedicalSummary?.get("MedicalItemsVitalSign")?.toArray() ?? [];
        this.immunizations = immunizations ?? [];
    }

    public getLatestPapTest(encounter: Encounter): MedicalItemScreeningTestResponse {
        const previousEncounters = this.encounters
            .filter(p => LuxonDateTimeParser.parse(p.ConsultationDate) <= LuxonDateTimeParser.parse(encounter.ConsultationDate));

        return MedicalItemScreeningTest.getLastCompletedPapTest(this.screenings, previousEncounters);
    }

    /**
     * Returns the last vital signs that fits the following conditions:
     * - The vital sign date is from the last hour before
     *   - the creation of the obstetrical file (obstetricalFileContributionDate); or
     *   - the consultation date of the encounter on which the obstetrical file is on (consultationDate)
     * - The vital sign date is on the same day as the encounter of the obstetrical file (same day as consultationDate)
     * - The vital sign is from the current encounter or a past one (Vital sign consultation date < consultationDate)
     * - The vital sign is not already linked to an obstetrical file
     */
    public getVitalSignFromLastHour(consultationDate: Date, obstetricalFileContributionDate: Date): MedicalItemVitalSign {
        const contributionDate = LuxonDateTimeParser.parse(obstetricalFileContributionDate);
        const oneHourBeforeFileWasCreated = contributionDate.minus({ hours: 1 }).until(contributionDate);

        const encounterConsultationDate = LuxonDateTimeParser.parse(consultationDate);
        const consultationDateDaySpan = encounterConsultationDate.startOf("day").until(encounterConsultationDate.endOf("day"));
        const oneHourBeforeEncounterConsultationDate = encounterConsultationDate.minus({ hours: 1 }).until(encounterConsultationDate);

        return this.vitalSigns
            .filter(p =>
                (
                    oneHourBeforeFileWasCreated.contains(LuxonDateTimeParser.parse(p.PharmaStartDate))
                    || oneHourBeforeEncounterConsultationDate.contains(LuxonDateTimeParser.parse(p.PharmaStartDate))
                )
                && consultationDateDaySpan.contains(LuxonDateTimeParser.parse(p.ConsultationDate))
                && LuxonDateTimeParser.parse(p.ConsultationDate) <= LuxonDateTimeParser.parse(consultationDate)
                && !p.IsLinkedToObstetricalFile
            )
            .sortByPropertyDesc(p => p.PharmaStartDate)
            .firstOrDefault();
    }

    public isLatestMedicalItemInEmrTemplate(vitalSign: ObstetricalFileVitalSign): boolean {
        if (isGuidNullOrUndefinedOrEmpty(vitalSign?.medicalItemId))
            return true;

        const medicalItemsInEmrTemplate = this.vitalSigns.filter(p => p.EMRTemplateId === vitalSign.emrTemplateId);
        const latestMedicalItem = PatientMedicalItemsHelper.getLatestSavedMedicalItemForEMRTemplate(medicalItemsInEmrTemplate);

        return vitalSign.medicalItemId === latestMedicalItem?.MedicalItemId;
    }

    public isUniqueVitalSignForEmrTemplate(emrTemplateId: string): boolean {
        return this.vitalSigns
            .filter(p => p.EMRTemplateId === emrTemplateId)
            .length === 1;
    }

    public getVitalSignBy(emrTemplateId: string, consultationDate: Date): MedicalItemVitalSign {
        return this.vitalSigns
            .filter(p => p.EMRTemplateId === emrTemplateId && p.ConsultationDate <= consultationDate)
            .sortByPropertyDesc(p => p.ConsultationDate)
            .firstOrDefault();
    }

    public getVitalSignsExcept(vitalSign: MedicalItemVitalSign): MedicalItemVitalSign[] {
        return this.vitalSigns.filter(p => p.Id !== vitalSign.Id);
    }

    public getThreeLatestVitalSignsWithWeight(consultationDateAtStartOfObstetricalFile: Date): MedicalItemVitalSign[] {
        return this.getThreeLatestVitalSignsBasedOn(
            consultationDateAtStartOfObstetricalFile,
            v => !!v.Weight
        );
    }

    public getThreeLatestVitalSignsWithHeight(consultationDateAtStartOfObstetricalFile: Date): MedicalItemVitalSign[] {
        return this.getThreeLatestVitalSignsBasedOn(
            consultationDateAtStartOfObstetricalFile,
            v => !!v.Height
        );
    }

    private getThreeLatestVitalSignsBasedOn(
        consultationDateAtStartOfObstetricalFile: Date,
        shouldMatch: (vitalSign: MedicalItemVitalSign) => boolean
    ): MedicalItemVitalSign[] {
        return this.vitalSigns
            .filter(p => shouldMatch(p) && p.PharmaStartDate <= consultationDateAtStartOfObstetricalFile)
            .sortByPropertyDesc(p => LuxonDateTimeParser.parse(p.PharmaStartDate))
            .slice(0, 3);
    }

    public hasInfluenzaImmunization(consultationDate: Date, consultationDateAtStartOfObstetricalFile: Date): boolean {
        if (!this.immunizations?.any())
            return false;

        const influenzaImmunogenType = "inf";
        const encounterConsultationDate = DateOnly.parseUnlessNull(consultationDate)?.toLuxon();
        const consultationDateAtStartOfObstetricalFileDateOnly = DateOnly.parseUnlessNull(consultationDateAtStartOfObstetricalFile)?.toLuxon();

        return this.immunizations
            .some(p =>
                p.Type?.toLowerCase().trim() === influenzaImmunogenType
                && !p.EMRTemplateIsInvalid
                && !p.IsRefused
                && this.isImmunizationAdministeredDuringPregnancy(p, encounterConsultationDate, consultationDateAtStartOfObstetricalFileDateOnly)
            );
    }

    public hasPertussisImmunization(consultationDate: Date, consultationDateAtStartOfObstetricalFile: Date): boolean {
        if (!this.immunizations?.any())
            return false;

        const pertussisImmunogenType = ["dcaT", "Tdap", "Ca", "aP", "DCaT", "DTaP"];
        const encounterConsultationDate = DateOnly.parseUnlessNull(consultationDate)?.toLuxon();
        const consultationDateAtStartOfObstetricalFileDateOnly = DateOnly.parseUnlessNull(consultationDateAtStartOfObstetricalFile)?.toLuxon();

        return this.immunizations.some(
            patientImm => pertussisImmunogenType.some(
                pertussisType =>
                    patientImm.Type?.toLowerCase().split(",").map(p => p.trim()).contains(pertussisType.toLowerCase().trim())
                    && !patientImm.EMRTemplateIsInvalid
                    && !patientImm.IsRefused
                    && this.isImmunizationAdministeredDuringPregnancy(patientImm, encounterConsultationDate, consultationDateAtStartOfObstetricalFileDateOnly)
            )
        );
    }

    private isImmunizationAdministeredDuringPregnancy(
        immunization: MedicalItemImmunization,
        consultationDate: DateTime,
        consultationDateAtStartOfObstetricalFile: DateTime
    ): boolean {
        const administrationDate = DateOnly.parseUnlessNull(immunization.AdministeredDate)?.toLuxon();
        const immunizationConsultationDate = DateOnly.parseUnlessNull(immunization.ConsultationDate)?.toLuxon();

        return (
            administrationDate && immunizationConsultationDate && consultationDate && consultationDateAtStartOfObstetricalFile
            && administrationDate >= consultationDateAtStartOfObstetricalFile
            && (
                administrationDate <= consultationDate
                || (
                    administrationDate.hasSame(consultationDate, "day")
                    && immunizationConsultationDate <= consultationDate
                )
            )
        );
    }
}

export { ObstetricalFileMedicalSummary };
